import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { formatApiErrorDetail } from "../lib/api";
import { Wrench, Loader2 } from "lucide-react";

const dest = (role) => (role === "admin" ? "/centrale" : role === "tecnico" ? "/tecnico" : "/area-clienti");

export default function Login() {
  const { t } = useLang();
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav(dest(user.role), { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      nav(dest(u.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white px-6 pb-10 pt-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-blue-600">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white"><Wrench className="h-5 w-5" /></div>
          <span className="font-display text-xl font-extrabold text-slate-900">ProntoSof<span className="text-orange-500">24</span></span>
        </div>
        <LangToggle />
      </div>

      <div className="mt-12">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("login")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("app_tagline")}</p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("email")}</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">{t("password")}</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:bg-white" />
        </div>
        {error && <p data-testid="login-error" className="text-sm font-medium text-rose-600">{error}</p>}
        <button data-testid="login-submit" disabled={busy} type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 font-bold text-white transition hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("login")}
        </button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="h-px flex-1 bg-slate-200" />OR<div className="h-px flex-1 bg-slate-200" /></div>
      <button data-testid="login-google" onClick={google}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 font-bold text-slate-700 transition hover:bg-slate-50">
        <img alt="" src="https://www.google.com/favicon.ico" className="h-4 w-4" />{t("login_google")}
      </button>

      <div className="mt-6 flex items-center justify-between text-sm">
        <Link data-testid="link-forgot" to="/forgot-password" className="font-semibold text-blue-600">{t("forgot_password")}</Link>
        <Link data-testid="link-register" to="/register" className="font-semibold text-slate-700">{t("register")}</Link>
      </div>

      <div className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <p className="font-bold">Account DEMO</p>
        <p>Cliente: cliente@prontosof24.it / Cliente123!</p>
        <p>Tecnico: tecnico@prontosof24.it / Tecnico123!</p>
        <p>Centrale: sofiane.manar@hotmail.it / Prontosof24!</p>
      </div>
    </div>
  );
}
